import { fetch, Proxy } from '@skinsoft/network';
import { identity, isEqual, merge as lodashMerge } from 'lodash';
import { empty, merge, Observable, of, throwError } from 'rxjs';
import {
    catchError,
    concatMap,
    delayWhen,
    distinctUntilChanged,
    filter,
    last,
    map,
    mapTo,
    switchMap,
    tap,
} from 'rxjs/operators';

import { IndexedDBCache } from './cache/indexeddb-cache';
import { Cache } from './cache/types';

type FragmentParser<T> = (response: any) => T[];

export interface DataStoreOptions {
    endpoint?: string;
    query?: Record<string, any>;
}

export const DEFAULT_PROXY = new Proxy();

export abstract class DataStore<T = any> {
    protected proxy = DEFAULT_PROXY;
    private cache: Cache<T>;
    private endpoint: string;
    private fragmentParser: FragmentParser<T>;

    constructor(
        endpoint: string,
        fragmentParser: FragmentParser<T> = identity,
        proxy = DEFAULT_PROXY,
        cache: Cache<T> = new IndexedDBCache<T>(),
    ) {
        this.fragmentParser = fragmentParser;
        this.endpoint = endpoint;
        this.cache = cache;
        this.proxy = proxy;
    }

    public fetchAll(options: DataStoreOptions = {}): Observable<T[]> {
        const searchParams = new URLSearchParams(options.query);
        searchParams.set('archived', 'false');

        const endpoint = options.endpoint || this.endpoint;
        const cacheKey = `${endpoint}?${searchParams.toString()}`;

        return merge(
            this.proxy.fetch(endpoint, searchParams).pipe(
                map(this.fragmentParser),
                tap(data => this.cache.write(cacheKey, data)),
                catchError(error => (fetch.isNetworkError(error) ? empty() : throwError(error))),
            ),
            this.cache$(cacheKey),
        ).pipe(distinctUntilChanged(isEqual));
    }

    public fetchOne(id: string, networkFirst = false): Observable<T> {
        const uri = `${this.endpoint}/${id}`;

        const network$ = this.proxy.fetch(uri, undefined, undefined, undefined, networkFirst).pipe(
            tap(data => this.cache.write(uri, [data as T])),
            last(),
            catchError(error => (fetch.isNetworkError(error) ? empty() : throwError(error))),
        );
        const cache$ = this.cache$(uri).pipe(map(data => data[0]));
        let source$ = merge(network$, cache$);

        if (networkFirst) {
            source$ = source$.pipe(delayWhen(() => network$));
        }

        return source$.pipe(distinctUntilChanged(isEqual));
    }

    public notifyOn(pattern: RegExp) {
        return this.cache.notifyOn(pattern);
    }

    public update(id: string, body: Partial<T>) {
        const uri = `${this.endpoint}/${id}`;

        return this.proxy
            .fetch(uri, undefined, 'PUT', body, true)
            .pipe(switchMap(() => this.fetchOne(id, true)));
    }

    public create(createBody?: Partial<T>, body?: Partial<T>) {
        const uri = `${this.endpoint}`;

        return this.proxy.fetch(uri, createBody, 'POST').pipe(
            concatMap(response => {
                if (!body) {
                    return of(response);
                }
                return this.update(response.id, lodashMerge(response, body));
            }),
            concatMap(response =>
                this.proxy.fetch(uri).pipe(
                    tap(data => this.cache.write(uri, [data as T])),
                    mapTo(response),
                ),
            ),
            switchMap(({ id }) => this.fetchOne(id)),
        );
    }

    private cache$(key: string): Observable<T[]> {
        return this.cache.read(key).pipe(filter(data => data !== undefined)) as Observable<T[]>;
    }
}
